import React, { Fragment, useContext, useEffect, useState } from "react";
import propTypes from "prop-types";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, GridLegacy as Grid } from "@mui/material";
import { FiCheckSquare, FiSquare } from "react-icons/fi";

import { api, IProductCompletedCreate, ProductCompleted, ProductSaleItem, ProductTypes, SourceApp } from "../../../../logic/api";

import { SessionContext } from "../../../../utility/useSession";
import { useDate } from "../../../../utility/useDate";

import { ProgressView } from "../../../../components/notifications/progress/ProgressView";
import ErrorDialog from "../../../../components/notifications/errorDialog/ErrorDialog";
import EditableGroup, { EditableGroupType } from "../../../../components/editable/EditableGroup/EditableGroup";
import EditableField, { EditableFieldTypes } from "../../../../components/editable/EditableField/EditableField";
import { Trans, useTranslation } from "react-i18next";
import { ReleaseFlag, useReleaseManagement } from "@/utility/feature-management";

const ProductsCompletedList: React.FunctionComponent<{
  bookingId: number;
  saleItems: ProductSaleItem[] | null;
  onCompletedListUpdated: () => void;
}> = (props) => {
  const isCompletedProductsFixEnabled = useReleaseManagement(ReleaseFlag.releaseCompletedProductsFix);

  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const productsCompletedApi = api.hooks.productsCompleted.list();
  const createProductCompletedApi = api.hooks.productsCompleted.create();
  const [showDialog, setShowDialog] = useState(false);
  const [productSaleItem, setProductSaleItem] = useState<ProductSaleItem | null>(null);
  const { localizeDate, toFormattedDateTimeString } = useDate();
  const [productCompletedCreate, setProductCompletedCreate] = useState<IProductCompletedCreate>({
    bookingId: props.bookingId,
    completedDate: localizeDate(new Date()).toISO() ?? "",
    remarks: "",
    productType: ProductTypes.default,
    sourceApp: SourceApp.frontEnd
  });
  const refreshCompletedProducts = () => {
    productsCompletedApi.send(session.current.context, {
      bookingId: props.bookingId
    });
  };
  useEffect(() => {
    refreshCompletedProducts();
  }, [props.saleItems]);

  if (props.saleItems == null) return <Fragment />;
  const productTypes: ProductTypes[] = [ProductTypes.electricVehicle, ProductTypes.valet,];
  // && f.voided!=true

  const completeProduct = () => {
    // setShowDialog(false);
    if (productSaleItem == null) return;
    createProductCompletedApi.send(session.current.context, productCompletedCreate, () => {
      refreshCompletedProducts();
      setShowDialog(false);
      props.onCompletedListUpdated();
    });
  };

  return (
    <div>
      {productSaleItem != null && (
        <Dialog open={showDialog}>
          <DialogTitle>
            <Trans i18nKey="product.setProductCompleted">
              Set {{ productName: productSaleItem.name }} completed
            </Trans>
          </DialogTitle>
          <DialogContent style={{ minHeight: 250 }}>
            <Fragment>
              <ErrorDialog err={createProductCompletedApi.err}></ErrorDialog>
              <ProgressView status={createProductCompletedApi.status}></ProgressView>

              <div>
                <EditableGroup<IProductCompletedCreate>
                  id="completedItemDetails"
                  testId="products-completed-list-group"
                  editType={EditableGroupType.alwaysEditing}
                  data={productCompletedCreate}
                  onDataChanged={async (data) => {
                    setProductCompletedCreate(data);
                    return true;
                  }}
                >
                  <Grid item container>
                    <EditableField<IProductCompletedCreate, string>
                      inputType={EditableFieldTypes.date}
                      label={t("product.labels.completedDate")}
                      onProvide={(data) => data.completedDate}
                      onChange={(data, value) => {
                        data.completedDate = value;
                      }}
                    />
                    <EditableField<IProductCompletedCreate, string>
                      inputType={EditableFieldTypes.time}
                      label={t("product.labels.completedTime")}
                      onProvide={(data) => data.completedDate}
                      onChange={(data, value) => {
                        data.completedDate = value;
                      }}
                    />
                  </Grid>

                </EditableGroup>

              </div>
            </Fragment>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDialog(false)}>{t("general.close")}</Button>
            <Button onClick={() => completeProduct()}>{t("general.update")}</Button>
          </DialogActions>
        </Dialog>
      )}
      {props.saleItems.filter((f) => productTypes.indexOf(f.productType) != -1 && f.voided == false).map((item, index) => {
        let completedItem: ProductCompleted | null = null;
        let color = "grey";
        let isLoading = false;
        if (productsCompletedApi.data != null) {
          if (isCompletedProductsFixEnabled) {
            completedItem = productsCompletedApi.data.filter((f) => {
              if (f.productSaleItem) {
                return f.productSaleItem?.id === item.id && f.productType === item.productType;
              }

              return f.productType === item.productType;
            })[0];
          } else {
            completedItem = productsCompletedApi.data.filter((f) => f.productType == item.productType)[0];
          }

          if (completedItem == null) {
            color = "red";
          } else {
            color = "green";
          }
        } else {
          isLoading = true;
        }

        const renderStatusIcon = () => {
          if (isLoading) {
            return (
              <CircularProgress size={14} />
            );
          }

          if (completedItem) {
            return <FiCheckSquare size={20} />;
          }

          return (
            <FiSquare
              style={{ cursor: "pointer" }}
              size={20}
              onClick={() => {
                setProductSaleItem(item);
                setProductCompletedCreate({
                  ...productCompletedCreate,
                  bookingId: props.bookingId,
                  productType: item.productType,
                  remarks: "",
                });
                setShowDialog(true);
              }}
            />
          );
        };

        return (
          <Box key={index} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", padding: "12px", gap: "4px" }}>
            <Box sx={{ fontWeight: "bold", color: color }}>
              {item.name}{completedItem == null ? "" : ` - Completed at ${toFormattedDateTimeString(new Date(completedItem.completedDate))}`}
            </Box>
            {renderStatusIcon()}
          </Box>
        );
      })}
    </div>
  );
};

ProductsCompletedList.propTypes = {
  saleItems: propTypes.any,
  bookingId: propTypes.number.isRequired,
  onCompletedListUpdated: propTypes.func.isRequired
};

export default ProductsCompletedList;
